/**
 * Cumulative net P&L, by trading day — ROADMAP 9.5.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE SERIES, AND THE COLOUR IS REDUNDANT ON PURPOSE
 *
 * The palette validator is unambiguous about green against red: ΔE 3.7 under
 * deuteranopia, against a normal-vision ΔE of 32. A red-green colourblind
 * reader cannot tell a winning curve from a losing one by hue, and roughly
 * one man in twelve is one.
 *
 * So the hue never carries the message on its own. Three things say it
 * without colour:
 *
 *   POSITION   a zero baseline runs the width of the plot, so above and
 *              below are a spatial fact rather than a tinted one
 *   SIGN       the end label is written "+₹7,808" or "-₹2,140", explicitly
 *   THE TABLE  the rows underneath are the same numbers, which is the table
 *              view the contrast warning obliges
 *
 * The green also lands at 2.77:1 against a white surface, under the 3:1 bar —
 * acceptable only because of those three, which is exactly the relief the
 * validator asks for rather than a reason to ignore it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CUMULATIVE, NOT PER-DAY BARS
 *
 * The question the curve answers is "is the account going up", which is a
 * running total. The DAY's own figure is not thrown away — it is on the
 * tooltip, so both readings are available without a second axis. Two y-scales
 * on one plot is the one thing never to do.
 *
 * MEASURED, NOT SCALED. The SVG is drawn at the pixel width the card actually
 * has, via one `ResizeObserver` that fires on a window resize and nothing
 * else. Stretching a fixed viewBox with `preserveAspectRatio="none"` would
 * squash the markers into ellipses and the stroke into an uneven line.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { compactInr } from '@/screens/terminal/quantity'
import type { JournalTrade } from '../types'

/** A trading day: what it made, and where that left the running total. */
export interface CurvePoint {
  /** `YYYY-MM-DD` in IST. */
  day: string
  /** Net for that day alone. */
  net: number
  /** Net since the first day on screen. */
  cumulative: number
}

/** IST calendar day of a closed trade. */
const istDay = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })

/**
 * Fold closed trades into one point per trading day, oldest first.
 *
 * Days with no closed trade are absent rather than drawn flat at zero: the
 * x axis is the days that HAPPENED, and inventing a weekend into the series
 * would stretch a quiet week into a plateau that looks like a drawdown.
 */
export function curveFrom(trades: readonly JournalTrade[]): CurvePoint[] {
  const byDay = new Map<string, number>()
  for (const t of trades) {
    if (t.closedAt === null) continue
    const day = istDay(t.closedAt)
    byDay.set(day, (byDay.get(day) ?? 0) + t.net)
  }
  let running = 0
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, net]) => {
      running += net
      return { day, net, cumulative: running }
    })
}

const dayLabel = (day: string): string =>
  new Date(`${day}T00:00:00+05:30`)
    .toLocaleDateString('en-GB', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short' })
    .toUpperCase()

const signed = (v: number): string => `${v >= 0 ? '+' : '-'}₹${compactInr(Math.abs(v))}`

const H = 56
const PAD = { top: 8, right: 68, bottom: 14, left: 8 }

/**
 * Drawn at this width until the box reports its own.
 *
 * Starting at zero meant the card painted empty for one frame and then
 * popped — and in any environment without `ResizeObserver` it would stay
 * empty forever, silently. A plausible width that is immediately corrected
 * is better than a gap that might never close.
 */
const ASSUMED_WIDTH = 640

export function EquityCurve({ trades }: { trades: readonly JournalTrade[] }) {
  const box = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(ASSUMED_WIDTH)
  const [hover, setHover] = useState<number | null>(null)

  useEffect(() => {
    const el = box.current
    if (!el || typeof ResizeObserver === 'undefined') return
    // The entry carries the size, so nothing here reads geometry back and
    // no frame pays for a forced layout.
    const ro = new ResizeObserver(([entry]) => {
      const measured = entry ? Math.floor(entry.contentRect.width) : 0
      // A zero is a box that has not been laid out yet, not a chart of no
      // width — keep the assumption rather than collapsing to nothing.
      if (measured > 0) setWidth(measured)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const points = useMemo(() => curveFrom(trades), [trades])

  const geometry = useMemo(() => {
    if (points.length === 0 || width <= 0) return null
    const innerW = Math.max(1, width - PAD.left - PAD.right)
    const innerH = H - PAD.top - PAD.bottom

    const values = points.map((p) => p.cumulative)
    // Zero is always in frame: a curve that never crosses it still has to
    // show which side of it the account is on.
    const high = Math.max(...values, 0)
    const low = Math.min(...values, 0)
    const span = high - low || 1

    const x = (i: number): number =>
      PAD.left + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW)
    const y = (v: number): number => PAD.top + ((high - v) / span) * innerH

    return {
      x,
      y,
      zero: y(0),
      line: points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.cumulative)}`).join(' '),
      area:
        `M${x(0)},${y(0)} ` +
        points.map((p, i) => `L${x(i)},${y(p.cumulative)}`).join(' ') +
        ` L${x(points.length - 1)},${y(0)} Z`,
    }
  }, [points, width])

  const last = points[points.length - 1]
  const up = (last?.cumulative ?? 0) >= 0
  const ink = up ? 'hsl(var(--success))' : 'hsl(var(--destructive))'
  const shown = hover !== null ? points[hover] : undefined

  return (
    <div className="jn-equity rounded-xl border border-border bg-card" data-testid="journal-curve">
      <div className="flex items-baseline justify-between">
        <h2 className="text-[9px] font-bold tracking-[0.16em] text-muted-foreground">
          NET P&amp;L, CUMULATIVE
        </h2>
        {/*
          The hovered day, or the running total. One slot, so the eye does not
          have to choose which number it is reading.
        */}
        <span
          className="jn-equity-readout num text-[10px] text-muted-foreground"
          title={shown ? `${dayLabel(shown.day)} · day ${signed(shown.net)} · total ${signed(shown.cumulative)}` : undefined}
          data-testid="journal-curve-readout"
        >
          {shown
            ? `${dayLabel(shown.day)} · day ${signed(shown.net)} · total ${signed(shown.cumulative)}`
            : points.length === 0
              ? 'nothing closed yet'
              : `${points.length} trading day${points.length === 1 ? '' : 's'}`}
        </span>
      </div>

      <div ref={box} className="mt-1.5">
        {geometry === null ? (
          <div className="flex h-[56px] items-center justify-center text-[11px] text-muted-foreground">
            {points.length === 0 ? 'No closed trades to plot.' : ''}
          </div>
        ) : (
          <svg
            width={width}
            height={H}
            role="img"
            aria-label={`Cumulative net profit and loss across ${points.length} trading days, ending at ${signed(last?.cumulative ?? 0)}`}
            onMouseLeave={() => setHover(null)}
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect()
              const innerW = Math.max(1, width - PAD.left - PAD.right)
              const t = (e.clientX - rect.left - PAD.left) / innerW
              const i = Math.round(t * Math.max(points.length - 1, 1))
              setHover(Math.max(0, Math.min(points.length - 1, i)))
            }}
          >
            <defs>
              <linearGradient id="equity-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={ink} stopOpacity="0.18" />
                <stop offset="100%" stopColor={ink} stopOpacity="0" />
              </linearGradient>
            </defs>

            {/* The baseline does the work colour cannot: above and below are
                positional, so the reading survives without hue. */}
            <line
              x1={PAD.left}
              x2={width - PAD.right}
              y1={geometry.zero}
              y2={geometry.zero}
              stroke="hsl(var(--border))"
              strokeWidth={1}
            />
            <text
              x={PAD.left}
              y={geometry.zero - 4}
              className="fill-muted-foreground text-[8px]"
            >
              0
            </text>

            <path d={geometry.area} fill="url(#equity-fill)" />
            <path
              d={geometry.line}
              fill="none"
              stroke={ink}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />

            {hover !== null && points[hover] && (
              <g>
                <line
                  x1={geometry.x(hover)}
                  x2={geometry.x(hover)}
                  y1={PAD.top - 6}
                  y2={H - PAD.bottom}
                  stroke="hsl(var(--muted-foreground))"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                />
                <circle
                  cx={geometry.x(hover)}
                  cy={geometry.y(points[hover].cumulative)}
                  r={4.5}
                  fill={ink}
                  stroke="hsl(var(--card))"
                  strokeWidth={2}
                />
              </g>
            )}

            {/* The end of the curve, labelled with its SIGN — the second
                thing that makes the colour redundant rather than load-bearing. */}
            {last && (
              <>
                <circle
                  cx={geometry.x(points.length - 1)}
                  cy={geometry.y(last.cumulative)}
                  r={3.5}
                  fill={ink}
                />
                <text
                  x={Math.min(geometry.x(points.length - 1) + 8, width - PAD.right + 6)}
                  y={geometry.y(last.cumulative) + 4}
                  className={cn('num text-[11px] font-bold', up ? 'fill-success' : 'fill-destructive')}
                >
                  {signed(last.cumulative)}
                </text>
              </>
            )}

            {/* First and last day only. A label under every point is noise on
                a week and unreadable on a quarter. */}
            <text x={PAD.left} y={H - 4} className="fill-muted-foreground text-[8.5px]">
              {dayLabel(points[0]!.day)}
            </text>
            {points.length > 1 && (
              <text
                x={width - PAD.right}
                y={H - 4}
                textAnchor="end"
                className="fill-muted-foreground text-[8.5px]"
              >
                {dayLabel(points[points.length - 1]!.day)}
              </text>
            )}
          </svg>
        )}
      </div>
    </div>
  )
}
